import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'framer-motion';
import { PhoneIcon, GlobeIcon, ShieldCheckIcon, LayoutDashboardIcon, UploadCloudIcon, FileTextIcon, CheckCircle2Icon, SearchIcon, TvIcon, CameraIcon, LockIcon, ZapIcon, TrendingDownIcon, SmileIcon, ClockIcon, FileXIcon, MapPinIcon, CalendarIcon, ServerIcon, ActivityIcon, ScaleIcon, ConstructionIcon, TruckIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { ServiceType } from './QuoteModal';
import { QuoteForm } from './QuoteForm';
export function HeroSection({
  onOpenQuote,
  onServiceClick,
  onNavigate
}: {
  onOpenQuote?: () => void;
  onServiceClick?: (type: ServiceType) => void;
  onNavigate?: (page: string) => void;
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [domainSearch, setDomainSearch] = useState('');
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const slides = [{
    id: 'internet-audit',
    title: 'Stop Overpaying for Business Internet',
    subtitle: 'Our AI-driven audit uncovers hidden fees and unused services in minutes.',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&h=800&fit=crop',
    label: 'Internet Savings'
  }, {
    id: 'tv-audit',
    title: 'Lower Your Commercial TV Bill',
    subtitle: 'Stop overpaying for commercial TV. We find the best packages and hidden savings.',
    image: 'https://images.unsplash.com/photo-1593784991095-a205069470b6?w=1600&h=800&fit=crop',
    label: 'TV Savings'
  }, {
    id: 'new-service',
    title: 'Enterprise-Grade New Installs',
    subtitle: 'From fiber internet to custom CRM systems, we build your digital foundation.',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1600&h=800&fit=crop',
    label: 'New Installation'
  }, {
    id: 'relocation',
    title: 'Seamless Business Relocation',
    subtitle: 'Transfer your entire IT infrastructure with zero downtime and expert coordination.',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&h=800&fit=crop',
    label: 'Moving & Relocation'
  }, {
    id: 'website-transfer',
    title: 'Expert Website Transfer & Design',
    subtitle: 'Seamlessly migrate your website or let us build a new digital presence for your brand.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1600&h=800&fit=crop',
    label: 'Website Services'
  }];
  const servicesList = [{
    id: 'internet-audit',
    label: 'Internet Savings',
    title: 'Internet Bill',
    prefix: 'Lower my',
    icon: ScaleIcon,
    iconBg: 'bg-[#22A39F]/10',
    iconColor: 'text-[#22A39F]'
  }, {
    id: 'tv-audit',
    label: 'TV Savings',
    title: 'TV Bill',
    prefix: 'Lower my',
    icon: TvIcon,
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-600'
  }, {
    id: 'new-service',
    label: 'New Installation',
    title: 'New Service',
    prefix: 'I need a',
    icon: ConstructionIcon,
    iconBg: 'bg-[#FFD700]/10',
    iconColor: 'text-[#011B33]',
    highlight: true
  }, {
    id: 'relocation',
    label: 'Seamless Relocation',
    title: 'Connection',
    prefix: 'Transfer my',
    icon: TruckIcon,
    iconBg: 'bg-[#011B33]/10',
    iconColor: 'text-[#011B33]'
  }, {
    id: 'website-transfer',
    label: 'Website Transfer',
    title: 'Website',
    prefix: 'Transfer my',
    icon: GlobeIcon,
    iconBg: 'bg-indigo-500/10',
    iconColor: 'text-indigo-600'
  }];
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, slides.length]);
  return <>
      <section className="relative min-h-[70vh] flex flex-col overflow-hidden bg-slate-900" data-id="element-149">
        {/* Carousel Background */}
        <AnimatePresence mode="wait" data-id="element-150">
          <motion.div key={currentSlide} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 1
        }} className="absolute inset-0 z-0" data-id="element-151">
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/60 to-transparent z-10" data-id="element-152" />
            <img src={slides[currentSlide].image} alt={slides[currentSlide].title} className="w-full h-full object-cover opacity-50" data-id="element-153" />
          </motion.div>
        </AnimatePresence>

        {/* Top Utility Bar */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-154">
          <div className="flex items-center justify-between py-8 border-b border-white/10" data-id="element-155">
            <div className="text-2xl font-black tracking-[0.15em] text-white" data-id="element-156">
              AMBIENT TELECOMS
            </div>
            <div className="hidden md:flex items-center gap-8 text-[10px] font-bold text-white/50 uppercase tracking-[0.2em]" data-id="element-157">
              <div className="flex items-center gap-2" data-id="element-158">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-159"></span>
                <span data-id="element-160">Systems: Active</span>
              </div>
              <span data-id="element-161">v4.0.2</span>
            </div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-20 w-full flex-1 flex flex-col justify-center pt-16" data-id="element-162">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-163">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-164">
              <div className="max-w-4xl" data-id="element-165">
                <div className="min-h-[320px] md:min-h-[280px]" data-id="element-166">
                  <AnimatePresence mode="wait" data-id="element-167">
                    <motion.div key={currentSlide} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -20
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-168">
                      <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#22A39F]/20 border border-[#22A39F]/30 text-[#22A39F] text-xs font-bold mb-6 backdrop-blur-sm" data-id="element-169">
                        {slides[currentSlide].label}
                      </div>
                      <h1 className="text-4xl md:text-6xl font-black text-white leading-[1.05] mb-6 tracking-tight" data-id="element-170">
                        {slides[currentSlide].title}
                      </h1>
                      <p className="text-xl text-slate-300 leading-relaxed mb-5 max-w-2xl" data-id="element-171">
                        {slides[currentSlide].subtitle}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              <div className="relative" data-id="element-172">
                <AnimatePresence mode="wait" data-id="element-173">
                  {!isAutoPlaying && slides[currentSlide].id === 'relocation' && <motion.div initial={{
                  opacity: 0,
                  scale: 0.95,
                  x: 20
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.95,
                  x: 20
                }} className="w-full max-w-md ml-auto" data-id="element-174">
                        <QuoteForm data-id="element-175" />
                      </motion.div>}

                  {!isAutoPlaying && slides[currentSlide].id === 'website-transfer' && <motion.div initial={{
                  opacity: 0,
                  scale: 0.95,
                  x: 20
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.95,
                  x: 20
                }} className="w-full max-w-md ml-auto bg-white p-8 rounded-2xl shadow-2xl" data-id="element-176">
                        <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-177">
                          Find Your Perfect Domain
                        </h3>
                        <div className="relative mb-6" data-id="element-178">
                          <input type="text" placeholder="Search for a domain..." className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#22A39F] focus:border-[#22A39F] outline-none transition-all pr-12" value={domainSearch} onChange={e => setDomainSearch(e.target.value)} data-id="element-179" />
                          <button className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#22A39F] text-white rounded-lg flex items-center justify-center hover:bg-[#1d8a87] transition-colors" data-id="element-180">
                            <SearchIcon className="w-5 h-5" data-id="element-181" />
                          </button>
                        </div>
                        <div className="flex flex-col gap-4" data-id="element-182">
                          <p className="text-sm text-slate-500 text-center" data-id="element-183">
                            Already have a domain?{' '}
                            <button onClick={() => onNavigate?.('website-design')} className="text-[#22A39F] font-bold hover:underline" data-id="element-184">
                              Transfer it to us
                            </button>
                          </p>
                        </div>
                      </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Service Cards Navigation */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 divide-y sm:divide-y-0 lg:divide-x divide-slate-100 bg-white overflow-hidden shadow-2xl mt-12 w-full outline-none" onBlur={() => setIsAutoPlaying(true)} tabIndex={-1} data-id="element-185">
            {servicesList.map((service, i) => {
            const isActive = currentSlide === i;
            return <motion.div key={service.id} whileHover={{
              backgroundColor: isActive ? 'rgba(241, 245, 249, 1)' : 'rgba(248, 250, 252, 1)'
            }} className={`py-8 px-6 flex flex-col items-center text-center gap-4 group cursor-pointer transition-colors relative ${isActive ? 'bg-slate-100' : 'bg-white'}`} onClick={() => {
              setCurrentSlide(i);
              setIsAutoPlaying(false);
            }} data-id="element-186">
                  <div className={`w-12 h-12 ${service.iconBg} rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-sm ${isActive ? 'ring-2 ring-[#22A39F]' : ''}`} data-id="element-187">
                    <service.icon className={`w-6 h-6 ${service.iconColor}`} data-id="element-188" />
                  </div>
                  <div className="min-w-0" data-id="element-189">
                    <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-1 truncate" data-id="element-190">
                      {service.label}
                    </h3>
                    <button onClick={e => {
                  e.stopPropagation();
                  setCurrentSlide(i);
                  setIsAutoPlaying(false);
                }} className="mt-2 bg-[#22A39F] hover:bg-[#1d8a87] text-white px-5 py-2 rounded-full text-xs font-bold transition-all shadow-sm hover:shadow-md" data-id="element-191">
                      {service.id === 'website-transfer' ? 'Buy domain' : 'Get Started'}
                    </button>
                  </div>
                  {isActive && <motion.div layoutId="active-indicator" className="absolute bottom-0 left-0 right-0 h-1 bg-[#22A39F]" data-id="element-192" />}
                </motion.div>;
          })}
          </div>
        </div>
      </section>
    </>;
}